<template>
  <section class="training-videos" aria-label="Schulungsvideos">
    <header class="video-heading">
      <div>
        <span class="video-eyebrow">Mitarbeiter-Weiterbildung</span>
        <h2>Schulungsvideos</h2>
        <p>Wissen auffrischen und Kursinhalte in deinem Tempo vertiefen.</p>
      </div>
      <v-btn v-if="canManage" outlined color="#58d388" @click="openEditor()">
        <v-icon left>mdi-video-plus-outline</v-icon>
        Video hinzufügen
      </v-btn>
    </header>

    <v-autocomplete
      v-if="canManage && studioOptions.length"
      v-model="studioFilter"
      :items="studioOptions"
      item-text="name"
      item-value="_id"
      dark
      color="#58d388"
      :menu-props="{ dark: true }"
      outlined
      clearable
      hide-details
      label="Nach Studio filtern"
      placeholder="Alle Studios"
      persistent-placeholder
      class="video-filter" />
    <v-select
      v-if="canManage"
      v-model="statusFilter"
      :items="statusOptions"
      dark
      outlined
      clearable
      hide-details
      label="Nach Status filtern"
      placeholder="Alle Status"
      persistent-placeholder
      class="video-filter" />
    <v-alert v-if="notice" type="success" text dismissible @input="notice = ''">{{ notice }}</v-alert>
    <v-alert v-if="error" type="error" text>
      {{ error }}
      <v-btn text @click="refresh">Erneut laden</v-btn>
    </v-alert>
    <div v-if="loading" class="video-empty">
      <v-progress-circular indeterminate color="#58d388" />
      <span>Videos werden geladen ...</span>
    </div>
    <div v-else-if="!error && !visibleVideos.length" class="video-empty">
      <v-icon color="#58d388">mdi-video-outline</v-icon>
      <p>
        {{
          canManage
            ? "Für diese Auswahl sind keine Schulungsvideos vorhanden."
            : "Hier erscheinen die Schulungsvideos, sobald sie für dich veröffentlicht sind."
        }}
      </p>
    </div>
    <div v-else-if="!error" class="video-grid">
      <article v-for="video in visibleVideos" :key="video._id" class="video-card">
        <div class="video-card-heading">
          <v-icon color="#58d388">mdi-play-circle-outline</v-icon>
          <h3>{{ video.title }}</h3>
        </div>
        <p v-if="video.description" class="video-summary">{{ video.description }}</p>
        <p v-if="canManage" class="video-secondary">
          {{ video.status === 'archived' ? 'Archiviert · Nur für die Verwaltung sichtbar' : 'Veröffentlicht' }}
        </p>
        <div class="video-studios" aria-label="Sichtbar in diesen Studios">
          <span v-if="!video.studios.length">Alle Studios</span>
          <span v-for="studio in video.studios" :key="studio._id">{{ studio.name }}</span>
        </div>
        <p v-if="canManage" class="video-secondary">
          {{ video.users && video.users.length ? `Nur für: ${video.users.map(userName).join(', ')}` : 'Alle Berechtigten der gewählten Studios' }}
        </p>
        <p v-if="!video.media" class="video-draft">Upload ausstehend · Nur für die Verwaltung sichtbar</p>
        <div class="video-actions">
          <v-btn v-if="video.media" text color="#58d388" @click="openPlayer(video)">
            <v-icon left>mdi-play</v-icon>
            Video ansehen
          </v-btn>
          <div v-if="canManage" class="video-manage">
            <v-btn dark icon :aria-label="`${video.title} bearbeiten`" @click="openEditor(video)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn
              dark
              icon
              :aria-label="`${video.title} löschen`"
              @click="confirmDelete(video)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </article>
    </div>

    <v-dialog v-model="editorOpen" :persistent="saving" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable max-width="1020">
      <v-card dark class="video-dialog video-editor">
        <header class="video-dialog-heading video-editor-heading">
          <div class="video-editor-title">
            <span class="video-editor-icon"><v-icon color="#79dfa0">mdi-video-outline</v-icon></span>
            <div>
              <span class="video-eyebrow">Mitarbeiter-Weiterbildung</span>
              <h2>{{ form.id ? "Schulungsvideo bearbeiten" : "Neues Schulungsvideo" }}</h2>
            </div>
          </div>
          <v-btn icon :disabled="saving" aria-label="Schliessen" @click="editorOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="video-editor-body">
          <v-alert v-if="formError" type="error" text>{{ formError }}</v-alert>
          <div class="video-editor-grid">
            <section class="video-editor-section" aria-labelledby="video-content-heading">
              <div class="video-section-heading">
                <h3 id="video-content-heading">Inhalt</h3>
                <p>Was sollen deine Mitarbeitenden lernen?</p>
              </div>
              <v-text-field
                v-model="form.title"
                dark
                color="#58d388"
                label="Titel"
                placeholder="z. B. Reformer – Grundlagen"
                outlined
                maxlength="160"
                hide-details="auto"
                :disabled="saving" />
              <v-textarea
                v-model="form.description"
                dark
                color="#58d388"
                label="Beschreibung (optional)"
                placeholder="Eine kurze Einführung zum Video …"
                outlined
                rows="3"
                auto-grow
                maxlength="5000"
                hide-details="auto"
                :disabled="saving"
                class="video-editor-field" />
              <div class="video-upload-panel">
                <div class="video-upload-heading">
                  <v-icon color="#79dfa0">mdi-cloud-upload-outline</v-icon>
                  <div><h4>Videodatei</h4><p>Bis zu 15 GB · Verarbeitung nach dem Upload</p></div>
                </div>
                <p v-if="form.media" class="video-current-file">
                  <v-icon small color="#79dfa0">mdi-check-circle-outline</v-icon>
                  <span>{{ form.media.originalName }}</span>
                </p>
                <v-file-input
                  v-model="file"
                  dark
                  color="#58d388"
                  :label="form.media ? 'Video ersetzen (optional)' : 'Video auswählen'"
                  accept="video/*,.mp4,.m4v,.mov,.webm,.avi,.3gp"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  outlined
                  show-size
                  hide-details="auto"
                  :disabled="saving" />
              </div>
            </section>

            <div class="video-editor-settings">
              <section class="video-editor-section" aria-labelledby="video-audience-heading">
                <div class="video-section-heading">
                  <h3 id="video-audience-heading">Zielgruppe</h3>
                  <p>Leere Auswahl bedeutet: alle Berechtigten.</p>
                </div>
                <v-autocomplete
                  :value="form.studios"
                  :items="editorStudios"
                  item-text="name"
                  item-value="_id"
                  dark
                  color="#58d388"
                  :menu-props="{ dark: true }"
                  label="Studios"
                  placeholder="Alle Studios"
                  persistent-placeholder
                  multiple
                  chips
                  deletable-chips
                  clearable
                  outlined
                  hide-details="auto"
                  :loading="studiosLoading"
                  :disabled="saving || studiosLoading || !!studioError"
                  @input="setStudios" />
                <v-alert v-if="studioError" type="warning" text class="mt-4">
                  {{ studioError }}
                  <v-btn text @click="loadStudios">Erneut laden</v-btn>
                </v-alert>
                <div class="video-editor-field video-user-search">
                  <user-search
                    v-if="editorOpen"
                    :key="searchKey"
                    label="Mitarbeiter suchen"
                    :return-user="true"
                    rights="gf_manageEmployeeCourses"
                    :disabled="saving || studiosLoading || !!studioError"
                    instructor
                    dark
                    outlined
                    @update="addUser" />
                  <p class="video-search-hint">Nach Name oder E-Mail suchen · ab 3 Zeichen</p>
                </div>
                <ul v-if="form.users.length" class="video-users">
                  <li v-for="user in form.users" :key="user._id">
                    <span>{{ userName(user) }}<small v-if="user.email">{{ user.email }}</small></span>
                    <v-btn icon :disabled="saving" :aria-label="`${userName(user)} entfernen`" @click="removeUser(user._id)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </li>
                </ul>
                <div class="video-audience-summary" role="status" aria-live="polite">
                  <v-icon small color="#79dfa0">mdi-account-group-outline</v-icon>
                  <span>
                    {{ form.users.length ? `${form.users.length} ${form.users.length === 1 ? 'Person ausgewählt' : 'Personen ausgewählt'}` : 'Alle berechtigten Mitarbeitenden' }}
                    <small>{{ form.studios.length ? `In ${form.studios.length} ${form.studios.length === 1 ? 'ausgewähltem Studio' : 'ausgewählten Studios'}` : 'In allen Studios' }}</small>
                  </span>
                </div>
                <v-alert v-if="invalidUsers.length" type="warning" text class="mt-4">
                  Nicht mehr verfügbare Personen: {{ invalidUsers.map(userName).join(', ') }}.
                  Bitte passe die Auswahl an. Ohne ausgewählte Personen gilt die Freigabe für alle Berechtigten der gewählten Studios.
                </v-alert>
              </section>

              <section class="video-editor-section video-publish-section" aria-labelledby="video-publish-heading">
                <div class="video-section-heading"><h3 id="video-publish-heading">Veröffentlichung</h3></div>
                <div class="video-status-options" role="group" aria-label="Veröffentlichungsstatus">
                  <button
                    v-for="option in statusOptions"
                    :key="option.value"
                    type="button"
                    class="video-status-option"
                    :class="{ 'video-status-option--selected': form.status === option.value }"
                    :aria-pressed="form.status === option.value"
                    :disabled="saving"
                    @click="form.status = option.value">
                    <v-icon small>{{ option.value === 'archived' ? 'mdi-archive-outline' : 'mdi-eye-outline' }}</v-icon>
                    <span>{{ option.text }}</span>
                    <v-icon v-if="form.status === option.value" small class="video-status-check">mdi-check-circle</v-icon>
                  </button>
                </div>
                <p class="video-status-hint" role="status" aria-live="polite">
                  {{ form.status === 'archived'
                    ? 'Nur in der Verwaltung sichtbar. Mitarbeitende sehen das Video erst, wenn du es veröffentlichst.'
                    : 'Nach dem Speichern für die ausgewählte Zielgruppe sichtbar.' }}
                </p>
              </section>
            </div>
          </div>
          <div v-if="saving" class="video-progress" role="status" aria-live="polite">
            <span>{{ saveLabel }}</span>
            <v-progress-linear :value="progress" :indeterminate="phase !== 'upload'" color="#58d388" rounded height="6" />
            <span class="video-secondary">Bitte lasse diese Seite während des Uploads geöffnet.</span>
          </div>
        </v-card-text>
        <v-card-actions class="video-dialog-actions video-editor-footer">
          <span class="video-save-note">
            <v-icon small>{{ form.status === 'archived' ? 'mdi-archive-outline' : 'mdi-eye-outline' }}</v-icon>
            {{ form.status === 'archived' ? 'Wird im Archiv gespeichert' : 'Wird veröffentlicht' }}
          </span>
          <div class="video-editor-buttons">
            <v-btn text :disabled="saving" @click="editorOpen = false">Abbrechen</v-btn>
            <v-btn
              color="#58d388"
              depressed
              class="black--text"
              :loading="saving"
              :disabled="saving || conflict || studiosLoading || !!studioError || !!invalidUsers.length"
              @click="save">
              <v-icon left small>mdi-check</v-icon>
              {{ file ? "Hochladen & speichern" : "Speichern" }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="playerOpen" max-width="1040" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="playerOpen && selectedVideo" dark class="video-dialog">
        <header class="video-dialog-heading">
          <h2>{{ selectedVideo.title }}</h2>
          <v-btn icon aria-label="Video schliessen" @click="playerOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text>
          <SecuredMedia
            :key="playerKey"
            :src="playbackPath"
            media-type="video"
            stream-playback
            :cache-key="playerKey"
            :show-app-download-action="false"
            eager
            @error="playbackError = true" />
          <v-alert v-if="playbackError" type="error" text class="mt-4">
            Das Video konnte nicht geladen werden. Bitte versuche es erneut.
            <v-btn
              text
              @click="retryPlayback">
              Erneut laden
            </v-btn>
          </v-alert>
          <p v-if="selectedVideo.description" class="video-description">{{ selectedVideo.description }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!deleteTarget" :persistent="deleting" max-width="520" @input="!$event && !deleting && (deleteTarget = null)">
      <v-card v-if="deleteTarget" dark class="video-dialog">
        <header class="video-dialog-heading"><h2>Video löschen?</h2></header>
        <v-card-text>
          <p>«{{ deleteTarget.title }}» wird aus den Schulungsvideos entfernt und das Video wird gelöscht.</p>
          <v-alert v-if="deleteError" type="error" text>{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions class="video-dialog-actions">
          <v-btn text :disabled="deleting" @click="deleteTarget = null">Abbrechen</v-btn>
          <v-btn color="error" :loading="deleting" @click="removeVideo">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"
import userSearch from "@/components/userSearch"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

const MAX_VIDEO_SIZE = 15 * 1024 * 1024 * 1024
const emptyForm = () => ({ id: null, title: "", description: "", studios: [], users: [], status: "archived", media: null, revision: 0 })
const errorMessage = (error, fallback) => (error.response && error.response.data && error.response.data.error) || error.message || fallback

export default {
  name: "EmployeeCourseVideos",
  components: { SecuredMedia, userSearch },
  data() {
    return {
      videos: [],
      studios: [],
      studioFilter: null,
      statusFilter: null,
      statusOptions: [{ text: "Veröffentlicht", value: "published" }, { text: "Archiviert", value: "archived" }],
      searchKey: 0,
      loading: false,
      error: "",
      notice: "",
      loadId: 0,
      studiosLoading: false,
      studioError: "",
      editorOpen: false,
      form: emptyForm(),
      formError: "",
      file: null,
      saving: false,
      phase: "",
      progress: 0,
      conflict: false,
      uploadController: null,
      disposed: false,
      playerOpen: false,
      selectedVideo: null,
      playbackError: false,
      playerAttempt: 0,
      deleteTarget: null,
      deleting: false,
      deleteError: "",
    }
  },
  computed: {
    canManage() {
      return userService.hasRight("gf_manageEmployeeCourses")
    },
    activeStudioId() {
      return this.$store.getters["studioStore/getSelectedStudioId"]
    },
    visibleVideos() {
      return this.videos.filter(
        (video) => (!this.studioFilter || !video.studios.length || video.studios.some((studio) => studio._id === this.studioFilter))
          && (!this.statusFilter || (video.status || "published") === this.statusFilter),
      )
    },
    invalidUsers() {
      return this.form.users.filter((user) => user.unavailable)
    },
    studioOptions() {
      const studios = new Map(this.studios.map((studio) => [studio._id, studio]))
      this.videos.forEach((video) => video.studios.forEach((studio) => studios.set(studio._id, studio)))
      return [...studios.values()].sort((a, b) => a.name.localeCompare(b.name, "de"))
    },
    editorStudios() {
      const studios = new Map(this.studioOptions.map((studio) => [studio._id, studio]))
      this.form.studios.forEach((id) => {
        if (!studios.has(id)) studios.set(id, { _id: id, name: "Studio nicht verfügbar" })
      })
      return [...studios.values()]
    },
    playbackPath() {
      return this.selectedVideo ? api.employeeCourses.videos.getPlaybackPath(this.selectedVideo._id) : ""
    },
    playerKey() {
      return this.selectedVideo ? `${this.selectedVideo._id}-${this.selectedVideo.updatedAt}-${this.playerAttempt}` : ""
    },
    saveLabel() {
      return this.phase === "upload"
        ? `Video wird hochgeladen: ${this.progress} %`
        : this.phase === "prepare"
        ? "Upload wird vorbereitet ..."
        : "Video wird gespeichert ..."
    },
  },
  watch: {
    activeStudioId() {
      this.playerOpen = false
      this.selectedVideo = null
      this.videos = []
      this.refresh()
    },
  },
  created() {
    this.refresh()
    if (this.canManage) this.loadStudios()
  },
  beforeDestroy() {
    this.disposed = true
    this.loadId += 1
    if (this.uploadController) this.uploadController.abort()
  },
  methods: {
    userName(user) {
      return `${user.vorname || ""} ${user.nachname || ""}`.trim() || user.email || "Person nicht verfügbar"
    },
    setStudios(studios) {
      this.form.studios = studios || []
      // Keep selected people: silently dropping the last one would broaden access to everyone.
      this.searchKey += 1
    },
    addUser(user) {
      if (this.saving || !user || !user._id) return
      if (!this.form.users.some((selected) => selected._id === user._id)) this.form.users.push(user)
      this.searchKey += 1
    },
    removeUser(id) {
      this.form.users = this.form.users.filter((user) => user._id !== id)
      this.searchKey += 1
    },
    async refresh() {
      const loadId = ++this.loadId
      this.loading = true
      this.error = ""
      try {
        const videos = await api.employeeCourses.videos.fetchVideos()
        if (loadId === this.loadId) {
          this.videos = videos || []
          if (this.selectedVideo) {
            this.selectedVideo = this.videos.find((video) => video._id === this.selectedVideo._id) || null
            if (!this.selectedVideo || !this.selectedVideo.media) this.playerOpen = false
          }
        }
      } catch (error) {
        if (loadId === this.loadId) this.error = errorMessage(error, "Videos konnten nicht geladen werden.")
      } finally {
        if (loadId === this.loadId) this.loading = false
      }
    },
    async loadStudios() {
      this.studiosLoading = true
      this.studioError = ""
      try {
        this.studios = (await api.fetchStudios()).filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
      } catch (error) {
        this.studioError = "Studios konnten nicht geladen werden. Bitte versuche es erneut."
      } finally {
        this.studiosLoading = false
      }
    },
    confirmDelete(video) {
      this.deleteTarget = video
      this.deleteError = ""
    },
    retryPlayback() {
      this.playerAttempt += 1
      this.playbackError = false
    },
    openPlayer(video) {
      this.selectedVideo = video
      this.playbackError = false
      this.playerAttempt = 0
      this.playerOpen = true
    },
    openEditor(video = null) {
      this.form = video
        ? {
            id: video._id,
            title: video.title,
            description: video.description,
            studios: video.studios.map((studio) => studio._id),
            users: (video.users || []).map((user) => ({ ...user })),
            status: video.status || "published",
            media: video.media,
            revision: video.revision,
          }
        : emptyForm()
      this.searchKey += 1
      this.file = null
      this.formError = ""
      this.conflict = false
      this.progress = 0
      this.editorOpen = true
    },
    async save() {
      if (this.saving || this.conflict || !this.canManage || this.studiosLoading || this.studioError || this.invalidUsers.length) return
      this.formError = ""
      if (!this.form.title.trim()) {
        this.formError = "Bitte einen Titel eingeben."
        return
      }
      if (!this.file && !this.form.media) {
        this.formError = "Bitte ein Video auswählen."
        return
      }
      if (this.file && (!this.file.size || this.file.size > MAX_VIDEO_SIZE)) {
        this.formError = "Das Video muss eine gültige Datei sein und darf maximal 15 GB gross sein."
        return
      }
      if (this.file && !(String(this.file.type || "").startsWith("video/") || /\.(mp4|m4v|mov|webm|avi|3gp)$/i.test(this.file.name))) {
        this.formError = "Bitte eine Videodatei auswählen."
        return
      }
      this.saving = true
      this.notice = ""
      this.phase = "prepare"
      this.progress = 0
      this.uploadController = new AbortController()
      let session = null
      const metadata = {
        title: this.form.title.trim(), description: this.form.description.trim(), studios: [...this.form.studios],
        users: this.form.users.map((user) => user._id), status: this.form.status,
      }
      try {
        if (!this.form.id) {
          const draft = await api.employeeCourses.videos.createVideo(metadata)
          this.form.id = draft._id
          this.form.revision = draft.revision
        }
        if (this.disposed) throw new Error("Upload abgebrochen.")
        if (this.file) {
          session = await api.employeeCourses.videos.prepareUpload(this.form.id, this.file)
          this.phase = "upload"
          await uploadCloudflareMedia(this.file, session, {
            signal: this.uploadController.signal,
            onProgress: (loaded, total) => {
              this.progress = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0
            },
          })
        }
        if (this.disposed) throw new Error("Upload abgebrochen.")
        this.phase = "save"
        await api.employeeCourses.videos.updateVideo(this.form.id, {
          ...metadata,
          revision: this.form.revision,
          ...(session ? { media: session } : {}),
        })
        this.editorOpen = false
        this.notice = "Schulungsvideo gespeichert."
      } catch (error) {
        this.formError = errorMessage(error, "Das Video konnte nicht gespeichert werden.")
        this.conflict = Boolean(error.response && error.response.status === 409)
        if (session) {
          try {
            const result = await api.employeeCourses.videos.cancelUpload(this.form.id, session)
            if (result.retained) {
              this.editorOpen = false
              this.notice = "Schulungsvideo gespeichert."
            } else {
              this.form.revision = result.revision
            }
          } catch (_) {
            this.conflict = true
            this.formError += " Bitte schliesse den Dialog und prüfe den Eintrag vor einem erneuten Versuch."
          }
        }
        if (this.conflict) this.formError += " Bitte schliesse den Dialog und öffne den Eintrag erneut."
      } finally {
        this.saving = false
        this.uploadController = null
        if (!this.disposed) await this.refresh()
      }
    },
    async removeVideo() {
      if (this.deleting || !this.deleteTarget) return
      this.deleting = true
      this.deleteError = ""
      try {
        await api.employeeCourses.videos.deleteVideo(this.deleteTarget._id, this.deleteTarget.revision)
        if (this.selectedVideo && this.selectedVideo._id === this.deleteTarget._id) this.playerOpen = false
        this.deleteTarget = null
        this.notice = "Schulungsvideo gelöscht."
        await this.refresh()
      } catch (error) {
        this.deleteError = errorMessage(error, "Das Video konnte nicht gelöscht werden.")
        await this.refresh()
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.training-videos {
  margin: 2rem 0;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.25rem;
  background: #171c19;
  color: #f4f7f5;
  font-size: 1rem;
}
.video-heading,
.video-dialog-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.video-heading {
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}
.video-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 600;
}
.video-heading h2,
.video-dialog h2 {
  font-size: 1.5rem;
  line-height: 1.3;
  margin: 0.25rem 0 0.5rem;
  overflow-wrap: anywhere;
}
.video-heading p {
  margin: 0;
  color: #bdc9c1;
  font-size: 1rem;
}
.video-filter {
  max-width: 28rem;
  margin-bottom: 1.5rem;
}
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.video-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  background: #202823;
}
.video-card-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.video-card h3 {
  font-size: 1.125rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.video-summary {
  margin: 1rem 0;
  font-size: 1rem;
  color: #d0d9d3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.video-studios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 1rem 0;
}
.video-studios span {
  font-size: 0.75rem;
  line-height: 1.5;
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
  background: rgba(88, 211, 136, 0.1);
  color: #b2e9c5;
  overflow-wrap: anywhere;
}
.video-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: auto;
}
.video-manage {
  display: flex;
  margin-left: auto;
}
.video-draft,
.video-secondary,
.video-file {
  font-size: 0.875rem;
  color: #c8d4cc;
  overflow-wrap: anywhere;
}
.video-file {
  margin-top: 1.5rem;
}
.video-users {
  padding: 0;
  margin: 1rem 0;
  list-style: none;
}
.video-users li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 1rem;
  overflow-wrap: anywhere;
}
.video-users li > span {
  min-width: 0;
}
.video-users small {
  display: block;
  font-size: 0.875rem;
  color: #c8d4cc;
}
.video-empty {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 0;
  font-size: 1rem;
  color: #c8d4cc;
}
.video-empty p {
  margin: 0;
}
.video-dialog {
  background: #1c231f !important;
  font-size: 1rem;
}
.video-dialog-heading {
  padding: 1.5rem;
}
.video-dialog .v-card__text {
  font-size: 1rem;
}
.video-dialog-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.5rem 1.5rem;
}
.video-description {
  margin: 1.5rem 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 1rem;
  line-height: 1.6;
}
.video-progress {
  display: grid;
  gap: 0.75rem;
  margin-top: 1.5rem;
  font-size: 0.875rem;
}
.video-dialog ::v-deep .v-messages {
  font-size: 0.875rem;
}
.video-dialog ::v-deep .v-counter,
.video-dialog ::v-deep .v-chip {
  font-size: 0.75rem;
}
.video-dialog ::v-deep .v-label,
.video-dialog ::v-deep input,
.video-dialog ::v-deep textarea {
  font-size: 1rem;
}
.video-card .v-btn,
.video-heading .v-btn,
.video-dialog .v-btn {
  font-size: 0.875rem;
  letter-spacing: normal;
}
.video-editor {
  max-height: calc(100vh - 3rem);
  max-height: calc(100dvh - 3rem);
  border: 1px solid rgba(190, 218, 199, 0.14);
  border-radius: 1.25rem;
  overflow: hidden;
}
.video-editor .video-editor-heading {
  align-items: center;
  flex-shrink: 0;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(190, 218, 199, 0.1);
}
.video-editor-title {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
}
.video-editor-title > div {
  min-width: 0;
}
.video-editor-title h2 {
  margin: 0.25rem 0 0;
  font-size: 1.375rem;
}
.video-editor-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(121, 223, 160, 0.2);
  border-radius: 0.875rem;
  background: rgba(88, 211, 136, 0.08);
}
.video-editor .video-editor-body {
  padding: 1.5rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: #f4f7f5;
}
.video-editor-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: 1.5rem;
}
.video-editor-section,
.video-editor-settings {
  min-width: 0;
}
.video-editor-settings {
  padding-left: 1.5rem;
  border-left: 1px solid rgba(190, 218, 199, 0.1);
}
.video-section-heading {
  margin-bottom: 1.25rem;
}
.video-section-heading h3,
.video-upload-heading h4 {
  margin: 0;
  color: #f4f7f5;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
}
.video-section-heading p,
.video-upload-heading p,
.video-search-hint,
.video-status-hint {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: #b5c3ba;
}
.video-editor-field {
  margin-top: 1.25rem;
}
.video-upload-panel {
  margin-top: 1.5rem;
  padding: 1.125rem;
  border: 1px dashed rgba(121, 223, 160, 0.3);
  border-radius: 0.875rem;
  background: rgba(88, 211, 136, 0.035);
}
.video-upload-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}
.video-upload-heading > .v-icon {
  margin-top: 0.25rem;
}
.video-current-file {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #c8d4cc;
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.video-current-file span {
  min-width: 0;
}
.video-user-search ::v-deep .v-text-field__details:has(.v-messages:not(.error--text)) {
  min-height: 0;
  margin-bottom: 0;
}
.video-search-hint {
  margin-top: 0.5rem;
}
.video-audience-summary {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border-radius: 0.75rem;
  background: rgba(88, 211, 136, 0.07);
  font-size: 0.875rem;
  line-height: 1.5;
  color: #d4e9db;
}
.video-audience-summary > .v-icon {
  margin-top: 0.125rem;
}
.video-audience-summary small {
  display: block;
  color: #b5c3ba;
  font-size: 0.875rem;
}
.video-editor .video-users {
  display: grid;
  gap: 0.5rem;
  margin: 1rem 0 0;
}
.video-editor .video-users li {
  padding: 0.5rem 0.625rem 0.5rem 0.875rem;
  border: 1px solid rgba(190, 218, 199, 0.12);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.025);
}
.video-publish-section {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(190, 218, 199, 0.1);
}
.video-publish-section .video-section-heading {
  margin-bottom: 0.75rem;
}
.video-status-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.video-status-option {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 10rem;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid rgba(190, 218, 199, 0.2);
  border-radius: 0.625rem;
  color: #becac2;
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
}
.video-status-option .v-icon {
  color: inherit;
}
.video-status-option:hover:not(:disabled) {
  border-color: #79dfa0;
  background: rgba(88, 211, 136, 0.06);
}
.video-status-option:focus-visible {
  outline: 2px solid #79dfa0;
  outline-offset: 3px;
}
.video-status-option--selected {
  border-color: #79dfa0;
  background: rgba(88, 211, 136, 0.1);
  color: #b9f1cc;
}
.video-status-option:disabled {
  cursor: default;
  opacity: 0.65;
}
.video-status-check {
  margin-left: auto;
}
.video-status-hint {
  margin-top: 0.75rem;
}
.video-editor .video-editor-footer {
  justify-content: space-between;
  flex-shrink: 0;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(190, 218, 199, 0.12);
  background: #19201c;
}
.video-save-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b5c3ba;
  font-size: 0.875rem;
  line-height: 1.5;
}
.video-save-note .v-icon {
  color: inherit;
}
.video-editor-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
}
.video-editor-buttons .v-btn {
  min-height: 2.75rem;
  border-radius: 0.625rem;
  text-transform: none;
}
.video-editor ::v-deep .v-input__slot {
  background: rgba(0, 0, 0, 0.08);
}
.video-editor ::v-deep .v-messages {
  line-height: 1.5;
}
.video-editor ::v-deep .v-select__selections {
  min-width: 0;
}
.video-editor ::v-deep .v-chip {
  max-width: 100%;
  height: auto;
  min-height: 2rem;
}
.video-editor ::v-deep .v-chip__content {
  height: auto;
  white-space: normal;
  overflow-wrap: anywhere;
}
@media (max-width: 850px) {
  .video-editor-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .video-editor-settings {
    padding-left: 0;
    padding-top: 1.5rem;
    border-left: 0;
    border-top: 1px solid rgba(190, 218, 199, 0.1);
  }
}
@media (max-width: 599px) {
  .video-editor {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0;
  }
  .video-editor .video-editor-heading,
  .video-editor .video-editor-body,
  .video-editor .video-editor-footer {
    padding: 1rem;
  }
  .video-editor-title {
    gap: 0.625rem;
  }
  .video-editor-title h2 {
    font-size: 1.25rem;
  }
  .video-editor-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
  .video-editor-buttons {
    width: 100%;
  }
  .video-editor-buttons .v-btn {
    flex: 1 1 auto;
  }
}
@media (max-width: 700px) {
  .training-videos {
    padding: 1rem;
  }
  .video-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .video-dialog-heading {
    padding: 1rem;
  }
  .video-dialog-actions {
    padding: 1rem;
  }
}
</style>
